type SuspensePromiseStatus = 'pending' | 'resolved' | 'rejected';

export interface SuspensePromise<T> {
  read(): T;
}

export interface CreateSuspensePromiseOptions<T> {
  defaultValue?: T;
  timeoutMs?: number;
  onError?: (error: unknown) => void;
}

const isPromiseLike = <T>(
  valueOrPromise: T | PromiseLike<T>,
): valueOrPromise is PromiseLike<T> => {
  return (
    typeof valueOrPromise === 'object' &&
    valueOrPromise !== null &&
    'then' in valueOrPromise &&
    typeof valueOrPromise.then === 'function'
  );
};

const withTimeoutPromise = <T>(
  promise: PromiseLike<T>,
  timeoutMs: number,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('[timeout] error: promise timed out')),
      timeoutMs,
    );
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
};

export const createSuspensePromise = <T>(
  valueOrPromise: T | PromiseLike<T>,
  options?: CreateSuspensePromiseOptions<T>,
): SuspensePromise<T> => {
  if (!isPromiseLike(valueOrPromise)) {
    return {
      read: () => valueOrPromise,
    };
  }

  let status: SuspensePromiseStatus = 'pending';
  let response: T;
  let error: unknown;

  const hasDefaultValue = options?.defaultValue !== undefined;
  const shouldUseTimeout = hasDefaultValue && options?.timeoutMs !== undefined;
  const trackedPromise = shouldUseTimeout
    ? withTimeoutPromise(valueOrPromise, options.timeoutMs as number)
    : valueOrPromise;

  const suspender = trackedPromise.then(
    (value) => {
      status = 'resolved';
      response = value;
    },
    (reason) => {
      options?.onError?.(reason);
      if (hasDefaultValue) {
        status = 'resolved';
        response = options.defaultValue as T;
        return;
      }

      status = 'rejected';
      error = reason;
    },
  );

  return {
    read() {
      if (status === 'pending') {
        throw suspender;
      }

      if (status === 'rejected') {
        throw error;
      }

      return response;
    },
  };
};
